import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, X } from "lucide-react";

/**
 * CMP (Consent Management Platform) MBOAZICK.
 * - Bannière sur première visite : Tout accepter / Tout refuser / Personnaliser
 * - Modale de préférences : Essentiels (imposés) / Statistiques / Publicité
 * - Persistance dans localStorage : { essential:true, analytics, advertising, ts }
 * - Émet un événement `mboa-consent-change` pour que d'éventuels scripts tiers
 *   (Google AdSense, Analytics…) puissent réagir sans recharger la page.
 *
 * Aucun cookie n'est déposé tant que le consentement n'a pas été explicitement donné.
 */
const STORAGE_KEY = "mboa_consent_v1";
const DEFAULTS = { essential: true, analytics: false, advertising: false };

export function getConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function saveConsent(consent) {
    const payload = { ...DEFAULTS, ...consent, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("mboa-consent-change", { detail: payload }));
    return payload;
}

/** Ouvre la modale de préférences depuis n'importe où (ex: footer). */
export function openCookiePreferences() {
    window.dispatchEvent(new CustomEvent("mboa-consent-open"));
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULTS);

    useEffect(() => {
        const existing = getConsent();
        if (!existing) {
            // Léger délai pour ne pas polluer le premier paint du site
            const t = setTimeout(() => setVisible(true), 1200);
            return () => clearTimeout(t);
        }
        setPrefs(existing);
    }, []);

    useEffect(() => {
        const openHandler = () => {
            setPrefs(getConsent() || DEFAULTS);
            setPrefsOpen(true);
        };
        window.addEventListener("mboa-consent-open", openHandler);
        return () => window.removeEventListener("mboa-consent-open", openHandler);
    }, []);

    const acceptAll = () => {
        saveConsent({ essential: true, analytics: true, advertising: true });
        setVisible(false);
        setPrefsOpen(false);
    };
    const rejectAll = () => {
        saveConsent({ essential: true, analytics: false, advertising: false });
        setVisible(false);
        setPrefsOpen(false);
    };
    const savePrefs = () => {
        saveConsent(prefs);
        setVisible(false);
        setPrefsOpen(false);
    };

    if (!visible && !prefsOpen) return null;

    return (
        <>
            {/* ============================== Bannière basse */}
            {visible && !prefsOpen && (
                <div
                    className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 z-[100] max-w-lg bg-[#0d0d0d] border border-[#D4AF37]/30 rounded-2xl shadow-2xl p-5 md:p-6 animate-in fade-in slide-in-from-bottom-4"
                    data-testid="cookie-consent-banner"
                    role="dialog"
                    aria-label="Consentement cookies"
                >
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                            <Cookie className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display text-base md:text-lg font-semibold">Cookies & confidentialité</h3>
                            <p className="mt-1.5 text-xs md:text-sm text-[#A8A8A8] leading-relaxed">
                                MBOAZICK utilise des cookies techniques (indispensables) et, avec ton accord, des cookies
                                pour mesurer l&apos;audience et diffuser des publicités pertinentes. Lis notre{" "}
                                <Link to="/privacy" className="text-[#D4AF37] underline hover:text-[#e6c14d]">
                                    politique de confidentialité
                                </Link>.
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={acceptAll}
                            className="flex-1 bg-[#00d97e] hover:bg-[#00f099] text-[#050505] font-bold px-4 py-2.5 rounded-full text-sm transition-colors"
                            data-testid="cookie-accept-all"
                        >
                            Tout accepter
                        </button>
                        <button
                            onClick={rejectAll}
                            className="flex-1 border border-[#262626] hover:border-[#F4F4F4] text-[#F4F4F4] font-semibold px-4 py-2.5 rounded-full text-sm transition-colors"
                            data-testid="cookie-reject-all"
                        >
                            Tout refuser
                        </button>
                    </div>
                    <button
                        onClick={() => { setPrefs(getConsent() || DEFAULTS); setPrefsOpen(true); }}
                        className="mt-3 w-full text-center text-xs text-[#A8A8A8] hover:text-[#F4F4F4] underline transition-colors"
                        data-testid="cookie-customize"
                    >
                        Personnaliser mes choix
                    </button>
                </div>
            )}

            {/* ============================== Modale préférences */}
            {prefsOpen && (
                <div
                    className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setPrefsOpen(false)}
                    data-testid="cookie-prefs-modal"
                >
                    <div
                        className="w-full max-w-lg bg-[#0d0d0d] border border-[#262626] rounded-2xl p-6 md:p-7 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="font-display text-xl md:text-2xl font-semibold flex items-center gap-2">
                                    <Cookie className="w-5 h-5 text-[#D4AF37]" /> Préférences cookies
                                </h3>
                                <p className="mt-1.5 text-xs text-[#A8A8A8]">
                                    Choisis les catégories de cookies que tu autorises. Tu peux modifier ces choix à
                                    tout moment depuis le pied de page.
                                </p>
                            </div>
                            <button
                                onClick={() => setPrefsOpen(false)}
                                className="text-[#6E6E6E] hover:text-[#F4F4F4] shrink-0"
                                aria-label="Fermer"
                                data-testid="cookie-prefs-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-3">
                            <Category
                                icon={<Shield className="w-4 h-4" />}
                                accent="#00d97e"
                                title="Essentiels"
                                desc="Requis pour la connexion, le panier et la sécurité. Impossibles à désactiver."
                                checked
                                disabled
                            />
                            <Category
                                icon={<BarChart3 className="w-4 h-4" />}
                                accent="#00c0ff"
                                title="Statistiques anonymes"
                                desc="Nous aident à comprendre comment tu utilises MBOAZICK pour améliorer l'expérience."
                                checked={prefs.analytics}
                                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                                testId="cookie-toggle-analytics"
                            />
                            <Category
                                icon={<Megaphone className="w-4 h-4" />}
                                accent="#D4AF37"
                                title="Publicité"
                                desc="Permettent de diffuser des publicités adaptées à tes centres d'intérêt (Google AdSense et partenaires)."
                                checked={prefs.advertising}
                                onChange={(v) => setPrefs((p) => ({ ...p, advertising: v }))}
                                testId="cookie-toggle-advertising"
                            />
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={rejectAll}
                                className="flex-1 border border-[#262626] hover:border-[#F4F4F4] text-[#F4F4F4] font-semibold px-4 py-2.5 rounded-full text-sm transition-colors"
                                data-testid="cookie-prefs-reject"
                            >
                                Tout refuser
                            </button>
                            <button
                                onClick={savePrefs}
                                className="flex-1 border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#D4AF37] font-semibold px-4 py-2.5 rounded-full text-sm transition-colors"
                                data-testid="cookie-prefs-save"
                            >
                                Enregistrer mes choix
                            </button>
                            <button
                                onClick={acceptAll}
                                className="flex-1 bg-[#00d97e] hover:bg-[#00f099] text-[#050505] font-bold px-4 py-2.5 rounded-full text-sm transition-colors"
                                data-testid="cookie-prefs-accept"
                            >
                                Tout accepter
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Category({ icon, accent, title, desc, checked, onChange, disabled, testId }) {
    return (
        <div className="flex items-start gap-3 bg-[#111] border border-[#262626] rounded-xl p-4">
            <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 border"
                style={{ background: `${accent}15`, color: accent, borderColor: `${accent}55` }}
            >
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{title}</p>
                <p className="mt-0.5 text-xs text-[#A8A8A8] leading-relaxed">{desc}</p>
            </div>
            <label className={`relative inline-flex items-center shrink-0 cursor-pointer ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!checked}
                    disabled={disabled}
                    onChange={(e) => onChange && onChange(e.target.checked)}
                    data-testid={testId}
                />
                <div
                    className="w-11 h-6 rounded-full peer-focus:ring-2 peer-focus:ring-[#D4AF37]/40 transition-colors bg-[#262626] peer-checked:bg-[#00d97e] relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5"
                />
            </label>
        </div>
    );
}
